import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import io from 'socket.io-client';

const AllConversations = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ─── State ───────────────────────────────────────────────────
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [userRole, setUserRole] = useState('agent');
  const [currentAgentName, setCurrentAgentName] = useState('');
  const [currentAgentId, setCurrentAgentId] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [typeFilter, setTypeFilter] = useState('all');

  // Chat panel
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [botPaused, setBotPaused] = useState(false);
  const [lastMessageIds, setLastMessageIds] = useState({});

  // Modals
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [takingConversation, setTakingConversation] = useState(null);

  // Canned responses
  const [cannedResponses, setCannedResponses] = useState([]);
  const [showCannedResponses, setShowCannedResponses] = useState(false);
  const [filteredCannedResponses, setFilteredCannedResponses] = useState([]);

  // WebSocket
  const [socketConnected, setSocketConnected] = useState(false);

  // Mobile
  const [isMobileChat, setIsMobileChat] = useState(false);

  // Theme detection
  const { isDark } = useTheme();

  // Theme-aware color palette
  const t = useMemo(() => isDark ? {
    bg: '#080b1e',
    panelBg: '#0d1229',
    panelBorder: 'rgba(255,255,255,0.06)',
    cardBg: 'rgba(255,255,255,0.04)',
    cardBorder: 'rgba(255,255,255,0.08)',
    cardHover: 'rgba(255,255,255,0.03)',
    inputBg: 'rgba(255,255,255,0.04)',
    inputBorder: 'rgba(255,255,255,0.08)',
    text: '#d1d5db',
    textStrong: '#e5e7eb',
    textMuted: '#9ca3af',
    textFaint: '#6b7280',
    msgBg: '#080b1e',
    headerBg: '#0d1229',
    selectedBg: 'rgba(99,102,241,0.12)',
    scrollThumb: 'rgba(255,255,255,0.1)',
    selectBg: '#12182f',
    modalBg: 'rgba(0,0,0,0.7)',
    modalPanel: '#12182f',
    emptyBg: '#080b1e',
  } : {
    bg: '#eeedf5',
    panelBg: '#f6f5fa',
    panelBorder: '#dedce8',
    cardBg: '#e8e6f0',
    cardBorder: '#dedce8',
    cardHover: '#e2e0ec',
    inputBg: '#e8e6f0',
    inputBorder: '#d0cede',
    text: '#2d2b3d',
    textStrong: '#1a1830',
    textMuted: '#6b6880',
    textFaint: '#9895aa',
    msgBg: '#e4e2ee',
    headerBg: '#f6f5fa',
    selectedBg: 'rgba(99,102,241,0.08)',
    scrollThumb: 'rgba(0,0,0,0.12)',
    selectBg: '#f6f5fa',
    modalBg: 'rgba(0,0,0,0.4)',
    modalPanel: '#f6f5fa',
    emptyBg: '#eeedf5',
  }, [isDark]);

  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioChunksRef = useRef([]);
  const socketRef = useRef(null);
  const shouldScrollRef = useRef(false);
  const isAtBottomRef = useRef(true); // Track if user is at bottom of chat
  const prevMessagesLenRef = useRef(0); // Track previous message count
  const selectedConversationRef = useRef(null);
  const messagePollingRef = useRef(null);
  const conversationPollingRef = useRef(null);
  const searchInputRef = useRef(null);
  const notificationSoundRef = useRef(null);
  const MESSAGES_POLL_INTERVAL = 2000;

  // ─── Browser Notification Permission ──────────────────────────
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().then(perm => setNotifPermission(perm));
    }
    // AudioContext must be created after user gesture
    const initAudio = () => {
      if (!notificationSoundRef.current) {
        try {
          notificationSoundRef.current = new (window.AudioContext || window.webkitAudioContext)();
          console.log('🔊 AudioContext initialized');
        } catch (e) {}
      }
      document.removeEventListener('click', initAudio);
    };
    document.addEventListener('click', initAudio);
    return () => document.removeEventListener('click', initAudio);
  }, []);

  const playNotificationSound = useCallback((type = 'message') => {
    try {
      const ctx = notificationSoundRef.current;
      if (!ctx) return;
      // Resume context if suspended (browser policy)
      if (ctx.state === 'suspended') ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.value = 0.15;
      
      if (type === 'urgent') {
        // Double beep for urgent (transfer request, new ticket)
        osc.frequency.value = 880;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
        gain.gain.setValueAtTime(0.15, ctx.currentTime + 0.2);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.4);
      } else {
        // Single soft ding for messages
        osc.frequency.value = 660;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.12, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.35);
      }
    } catch (e) { /* ignore audio errors */ }
  }, []);

  const sendBrowserNotification = useCallback((title, body, options = {}) => {
    // Play sound regardless of permission
    playNotificationSound(options.urgent ? 'urgent' : 'message');

    // Send browser notification if permitted and tab not focused
    if (notifPermission !== 'granted') return;
    if (document.hasFocus() && !options.alwaysShow) return; // Don't notify if tab is focused (unless forced)

    try {
      const notif = new Notification(title, {
        body: body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: options.tag || 'matix-' + Date.now(),
        renotify: !!options.tag,
        silent: true, // We play our own sound
        ...options
      });
      
      // Auto-close after 8 seconds
      setTimeout(() => notif.close(), 8000);
      
      // Focus window on click
      notif.onclick = () => {
        window.focus();
        notif.close();
        if (options.conversationId) {
          // Find and select the conversation
          const conv = conversations.find(c => c.id === parseInt(options.conversationId));
          if (conv) handleConversationClick(conv);
        }
      };
    } catch (e) {
      console.log('Notification error:', e);
    }
  }, [notifPermission, conversations, playNotificationSound]);

  // ─── Auth helpers ────────────────────────────────────────────
  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const getUserInfo = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || 'agent');
        setCurrentAgentName(payload.full_name || payload.name || 'Agent');
        setCurrentAgentId(payload.id || payload.agent_id);
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }
  };

  // ─── API Calls ───────────────────────────────────────────────
  const fetchAllConversations = async (silent = false) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/v1/conversations?limit=100', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      if (data.success) {
        const convs = data.data.conversations || data.data || [];
        setConversations(convs);

        // Update selected conversation data if open — only if something meaningful changed
        if (selectedConversation) {
          const updated = convs.find(c => c.id === selectedConversation.id);
          if (updated) {
            // Only update if status, agent, or message count actually changed
            const needsUpdate = 
              updated.status !== selectedConversation.status ||
              updated.current_agent_name !== selectedConversation.current_agent_name ||
              updated.total_messages !== selectedConversation.total_messages ||
              updated.bot_paused !== selectedConversation.bot_paused;
            if (needsUpdate) {
              setSelectedConversation(prev => ({ ...prev, ...updated }));
            }
          }
        }
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      if (!silent) setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchAgents = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch('/api/v1/agents', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          setAgents(data.data);
        }
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  const fetchCannedResponses = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch('/api/v1/canned-responses', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && Array.isArray(data.data)) {
          setCannedResponses(data.data);
        }
      }
    } catch (err) {
      console.error('Error fetching canned responses:', err);
    }
  };

  const fetchMessages = async (conversationId, silent = false) => {
    const token = localStorage.getItem('token');
    if (!silent) {
      setLoadingMessages(true);
      setMessages([]);
    }

    try {
      const response = await fetch(`/api/v1/conversations/${conversationId}/messages?limit=100`, {
        headers: getHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && Array.isArray(data.data)) {
          if (silent) {
            // Only append truly new messages — don't replace the array
            setMessages(prev => {
              const existingIds = new Set(prev.map(m => m.id));
              const newMsgs = data.data.filter(m => !existingIds.has(m.id));
              if (newMsgs.length > 0) {
                return [...prev, ...newMsgs].sort((a, b) =>
                  new Date(a.timestamp) - new Date(b.timestamp)
                );
              }
              return prev; // Same reference = no re-render = scroll preserved
            });
          } else {
            setMessages(data.data);
            if (data.data.length > 0) {
              const maxId = Math.max(...data.data.map(m => m.id));
              setLastMessageIds(prev => ({ ...prev, [conversationId]: maxId }));
            }
          }

          if (socketRef.current && socketConnected) {
            socketRef.current.emit('viewing-conversation', conversationId);
          }
        } else {
          if (!silent) setMessages([]);
        }
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  };

  // Keep refs in sync to avoid re-creating poll callback
  const lastMessageIdsRef = useRef({});
  useEffect(() => { lastMessageIdsRef.current = lastMessageIds; }, [lastMessageIds]);
  useEffect(() => { selectedConversationRef.current = selectedConversation; }, [selectedConversation]);

  const pollForNewMessages = useCallback(async () => {
    const conv = selectedConversationRef.current;
    if (!conv) return;
    try {
      const token = localStorage.getItem('token');
      const conversationId = conv.id;
      
      // Use the actual last message ID so server only returns truly NEW messages
      const lastId = lastMessageIdsRef.current[conversationId] || 0;
      const response = await fetch(`/api/v1/conversations/${conversationId}/messages?after_id=${lastId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && Array.isArray(data.data) && data.data.length > 0) {
          setMessages(prevMessages => {
            const existingIds = new Set(prevMessages.map(m => m.id));
            const newMessages = data.data.filter(msg => !existingIds.has(msg.id));
            if (newMessages.length > 0) {
              const maxId = Math.max(...newMessages.map(m => m.id));
              setLastMessageIds(prev => ({ ...prev, [conversationId]: maxId }));
              const allMessages = [...prevMessages, ...newMessages].sort((a, b) =>
                new Date(a.timestamp) - new Date(b.timestamp)
              );
              return allMessages;
            }
            return prevMessages; // Same reference = no re-render = scroll preserved
          });
        }
      }
    } catch (error) {
      console.error('Error polling messages:', error);
    }
  }, []); // ← No dependencies! Uses refs only — never gets recreated

  // ─── Actions ─────────────────────────────────────────────────
  // Emit queue-update to notify all other agents after local actions
  const broadcastQueueUpdate = () => {
    if (socketRef.current && socketConnected) {
      socketRef.current.emit('queue-update');
    }
  };

  const handleTakeConversation = async (conversationId, e) => {
    e?.stopPropagation();
    const token = localStorage.getItem('token');
    setTakingConversation(conversationId);

    try {
      const response = await fetch(`/api/v1/conversations/${conversationId}/assign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        await fetchAllConversations(true);
        broadcastQueueUpdate(); // Notify all other agents
        showNotification('Conversation taken successfully!', 'success');
      } else {
        const data = await response.json();
        showNotification(data.message || 'Failed to take conversation', 'error');
      }
    } catch (err) {
      console.error('Error taking conversation:', err);
      showNotification('Failed to take conversation', 'error');
    } finally {
      setTakingConversation(null);
    }
  };

  const handleAssignToAgent = async () => {
    if (!selectedAgent || !selectedConversation) return;
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`/api/v1/conversations/${selectedConversation.id}/assign-to`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_id: selectedAgent })
      });

      if (response.ok) {
        await fetchAllConversations(true);
        setShowAssignModal(false);
        setSelectedAgent('');
        broadcastQueueUpdate(); // Notify all other agents
        showNotification('Conversation assigned successfully!', 'success');
      } else {
        const data = await response.json();
        showNotification(data.message || 'Failed to assign conversation', 'error');
      }
    } catch (err) {
      console.error('Error assigning conversation:', err);
      showNotification('Failed to assign conversation', 'error');
    }
  };

  const completeConversation = async () => {
    if (!selectedConversation) return;
    if (!window.confirm('Are you sure you want to close this conversation?')) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/v1/conversations/${selectedConversation.id}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (response.ok && data.success) {
        showNotification('Conversation closed', 'success');
        if (socketRef.current && socketConnected) {
          socketRef.current.emit('conversation-completed', { conversation_id: selectedConversation.id });
        }
        broadcastQueueUpdate(); // Notify all other agents
        setSelectedConversation(null);
        setMessages([]);
        setIsMobileChat(false);
        fetchAllConversations(true);
      } else {
        showNotification(data.message || 'Failed to complete', 'error');
      }
    } catch (err) {
      console.error('Error completing conversation:', err);
      showNotification('Network error', 'error');
    }
  };

  const toggleBot = async () => {
    if (!selectedConversation) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/conversations/${selectedConversation.id}/toggle-bot`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setBotPaused(data.bot_paused);
        showNotification(data.bot_paused ? 'Bot paused' : 'Bot resumed', 'success');
        broadcastQueueUpdate(); // Notify others about status change
      }
    } catch (error) {
      console.error('Error toggling bot:', error);
      showNotification('Failed to toggle bot', 'error');
    }
  };

  const toggleConversationType = async () => {
    if (!selectedConversation) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/conversations/${selectedConversation.id}/toggle-type`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setSelectedConversation(prev => ({ ...prev, conversation_type: data.conversation_type }));
        setConversations(prev => prev.map(c => c.id === selectedConversation.id ? { ...c, conversation_type: data.conversation_type } : c));
        showNotification(`Changed to ${data.conversation_type}`, 'success');
      }
    } catch (error) {
      console.error('Error toggling type:', error);
      showNotification('Failed to change type', 'error');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || sendingMessage) return;

    const token = localStorage.getItem('token');
    const messageText = newMessage.trim();
    setSendingMessage(true);
    setNewMessage('');

    try {
      const conversationId = selectedConversation.id;
      const response = await fetch(`/api/v1/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: messageText, message_type: 'agent' })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        if (data.data) {
          const serverMessage = data.data;
          setMessages(prev => {
            const exists = prev.some(m => m.id === serverMessage.id);
            if (!exists) {
              const updated = [...prev, serverMessage].sort((a, b) =>
                new Date(a.timestamp) - new Date(b.timestamp)
              );
              setLastMessageIds(prevIds => ({ ...prevIds, [conversationId]: serverMessage.id }));
              return updated;
            }
            return prev;
          });

          if (socketRef.current && socketConnected) {
            socketRef.current.emit('message-sent', { conversation_id: conversationId, message: serverMessage });
          }
        }

        broadcastQueueUpdate(); // Update message counts for other agents

        if (data.whatsapp_sent) {
          showNotification('Message sent to customer!', 'success');
        } else if (data.whatsapp_error) {
          showNotification(`Saved but WhatsApp failed: ${data.whatsapp_error}`, 'warning');
        } else {
          showNotification('Message sent!', 'success');
        }

        scrollToBottom();
        shouldScrollRef.current = true;
        setBotPaused(true);
      } else {
        setNewMessage(messageText);
        showNotification(data.message || 'Failed to send', 'error');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setNewMessage(messageText);
      showNotification('Network error sending message', 'error');
    } finally {
      setSendingMessage(false);
      setTimeout(() => messageInputRef.current?.focus(), 50);
    }
  };

  const handleMediaUpload = async (file) => {
    if (!file || !selectedConversation) return;
    
    // Show preview for images
    if (file.type.startsWith('image/')) {
      setMediaPreview({ file, url: URL.createObjectURL(file), type: 'image' });
    } else {
      setMediaPreview({ file, url: null, type: 'document', name: file.name });
    }
  };

  const sendMedia = async () => {
    if (!mediaPreview || !selectedConversation || uploadingMedia) return;
    
    const token = localStorage.getItem('token');
    const conversationId = selectedConversation.id;
    setUploadingMedia(true);
    
    try {
      const formData = new FormData();
      formData.append('file', mediaPreview.file);
      if (newMessage.trim()) {
        formData.append('caption', newMessage.trim());
      }
      
      const response = await fetch(`/api/v1/conversations/${conversationId}/send-media`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        if (data.data) {
          setMessages(prev => {
            const exists = prev.some(m => m.id === data.data.id);
            if (!exists) {
              return [...prev, data.data].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            }
            return prev;
          });
        }
        setMediaPreview(null);
        setNewMessage('');
        
        if (data.whatsapp_sent) {
          showNotification('Media sent to customer!', 'success');
        } else if (data.whatsapp_error) {
          showNotification(`Saved but WhatsApp failed: ${data.whatsapp_error}`, 'warning');
        }
        scrollToBottom();
      } else {
        showNotification(data.message || 'Failed to send media', 'error');
      }
    } catch (err) {
      console.error('Error uploading media:', err);
      showNotification('Network error uploading media', 'error');
    } finally {
      setUploadingMedia(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const cancelMediaPreview = () => {
    if (mediaPreview?.url) URL.revokeObjectURL(mediaPreview.url);
    setMediaPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Use ogg/opus (WhatsApp native) if supported, fallback to webm
      const mimeType = MediaRecorder.isTypeSupported('audio/ogg;codecs=opus') 
        ? 'audio/ogg;codecs=opus' 
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      console.log('Recording with mimeType:', mimeType);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const recMime = mediaRecorder.mimeType || 'audio/ogg';
        const ext = recMime.includes('ogg') ? 'ogg' : 'webm';
        const blob = new Blob(audioChunksRef.current, { type: recMime });
        const url = URL.createObjectURL(blob);
        const file = new File([blob], `voice_${Date.now()}.${ext}`, { type: recMime });
        setMediaPreview({ file, url, type: 'voice' });
        stream.getTracks().forEach(track => track.stop());
        clearInterval(recordingTimerRef.current);
        setRecordingTime(0);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Microphone access denied:', err);
      showNotification('Microphone access denied', 'error');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream?.getTracks().forEach(track => track.stop());
    }
    setIsRecording(false);
    clearInterval(recordingTimerRef.current);
    setRecordingTime(0);
    audioChunksRef.current = [];
  };

  const formatRecordingTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return m + ':' + s;
  };

  const insertCannedResponse = async (response) => {
    const content = selectedConversation?.language === 'ar'
      ? (response.content_ar || response.content_en)
      : (response.content_en || response.content_ar);
    setNewMessage(content);
    setShowCannedResponses(false);

    const token = localStorage.getItem('token');
    try {
      await fetch(`/api/v1/canned-responses/${response.id}/use`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) { /* silent */ }
    messageInputRef.current?.focus();
  };

  // ─── Helpers ─────────────────────────────────────────────────
  const scrollToBottom = (force = false) => {
    requestAnimationFrame(() => {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  };

  // Track scroll position and if user is at bottom
  const savedScrollTopRef = useRef(0);
  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    savedScrollTopRef.current = container.scrollTop;
    const threshold = 150;
    isAtBottomRef.current = (container.scrollHeight - container.scrollTop - container.clientHeight) < threshold;
  };

  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    notification.style.cssText = `
      position:fixed;top:20px;right:20px;padding:12px 20px;border-radius:10px;
      color:${type === 'warning' ? '#000' : '#fff'};font-weight:600;z-index:9999;
      max-width:320px;font-size:13px;backdrop-filter:blur(10px);
      box-shadow:0 4px 20px rgba(0,0,0,0.3);
      background-color:${type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : type === 'warning' ? '#f59e0b' : '#6366f1'};
      animation:slideInRight 0.3s ease-out;
    `;
    notification.textContent = message;
    document.body.appendChild(notification);
    setTimeout(() => {
      if (document.body.contains(notification)) {
        notification.style.opacity = '0';
        notification.style.transition = 'opacity 0.3s ease';
        setTimeout(() => { if (document.body.contains(notification)) document.body.removeChild(notification); }, 300);
      }
    }, 3000);
  };

  const getTimeSince = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const then = new Date(timestamp);
    const minutes = Math.floor((now - then) / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const getDateLabel = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    
    const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    
    if (isSameDay(date, today)) return 'Today';
    if (isSameDay(date, yesterday)) return 'Yesterday';
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getDateKey = (timestamp) => {
    if (!timestamp) return '';
    const d = new Date(timestamp);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  };

  const getInitials = (name, phone) => {
    if (name && name !== 'Unknown Customer') {
      return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
    }
    return phone ? phone.slice(-2) : '??';
  };

  const getAvatarColor = (id) => {
    const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#06b6d4'];
    return colors[(id || 0) % colors.length];
  };

  // ─── Status config ───────────────────────────────────────────
  const STATUS_CONFIG = {
    normal:    { label: 'AI Bot',    color: '#6366f1', bg: 'rgba(99,102,241,0.15)',  border: 'rgba(99,102,241,0.4)' },
    assigned:  { label: 'Assigned',  color: '#10b981', bg: 'rgba(16,185,129,0.15)',  border: 'rgba(16,185,129,0.4)' },
    pending:   { label: 'Pending',   color: '#f59e0b', bg: 'rgba(245,158,11,0.15)',  border: 'rgba(245,158,11,0.4)' },
    completed: { label: 'Closed', color: '#6b7280', bg: 'rgba(107,114,128,0.15)', border: 'rgba(107,114,128,0.4)' },
    ended:     { label: 'Ended',     color: '#6b7280', bg: 'rgba(107,114,128,0.15)', border: 'rgba(107,114,128,0.4)' },
  };

  // ─── Filtering & sorting ─────────────────────────────────────
  const filteredConversations = useMemo(() => {
    let result = [...conversations];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c =>
        (c.phone_number || c.session_id || '').toLowerCase().includes(q) ||
        (c.user_name || '').toLowerCase().includes(q)
      );
    }

    if (statusFilter === 'all') {
      // "All" means all EXCEPT closed (completed/ended)
      result = result.filter(c => c.status !== 'completed' && c.status !== 'ended');
    } else if (statusFilter === 'completed') {
      // "Closed" includes both completed and ended
      result = result.filter(c => c.status === 'completed' || c.status === 'ended');
    } else {
      result = result.filter(c => c.status === statusFilter);
    }

    if (assignmentFilter === 'mine') {
      result = result.filter(c => c.current_agent_name === currentAgentName);
    } else if (assignmentFilter === 'unassigned') {
      result = result.filter(c => !c.current_agent_name && c.status !== 'completed' && c.status !== 'ended');
    } else if (assignmentFilter === 'unread') {
      result = result.filter(c => (c.unread_count || 0) > 0);
    }

    if (typeFilter === 'sales') {
      result = result.filter(c => c.conversation_type === 'sales');
    } else if (typeFilter === 'technical') {
      result = result.filter(c => c.conversation_type === 'technical');
    }

    result.sort((a, b) => {
      if (sortBy === 'unread') return (b.unread_count || 0) - (a.unread_count || 0);
      if (sortBy === 'messages') return (b.total_messages || 0) - (a.total_messages || 0);
      // Default: latest activity
      return new Date(b.last_activity || b.started_at || 0) - new Date(a.last_activity || a.started_at || 0);
    });

    return result;
  }, [conversations, searchQuery, statusFilter, assignmentFilter, sortBy, typeFilter, currentAgentName]);

  const statusCounts = useMemo(() => {
    const counts = { all: 0, normal: 0, assigned: 0, pending: 0, completed: 0, ended: 0 };
    conversations.forEach(c => {
      counts[c.status] = (counts[c.status] || 0) + 1;
      if (c.status !== 'completed' && c.status !== 'ended') counts.all++;
    });
    return counts;
  }, [conversations]);

  const totalUnread = useMemo(() =>
    conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0), [conversations]);

  // ─── Conversation selection ──────────────────────────────────
  const handleConversationClick = (conv) => {
    setSelectedConversation(conv);
    setIsMobileChat(true);
    setMessages([]);
    prevMessagesLenRef.current = 0; // Reset so initial load scrolls to bottom
    isAtBottomRef.current = true;   // Start at bottom
    fetchMessages(conv.id);

    // Fetch bot status
    const token = localStorage.getItem('token');
    fetch(`/api/v1/conversations/${conv.id}/bot-status`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(d => {
      if (d.success) setBotPaused(d.bot_paused);
    }).catch(() => setBotPaused(false));

    // Clear unread locally
    setConversations(prev => prev.map(c =>
      c.id === conv.id ? { ...c, unread_count: 0 } : c
    ));
  };

  // ─── Canned response shortcut detection ──────────────────────
  useEffect(() => {
    if (newMessage.startsWith('/')) {
      const shortcut = newMessage.substring(1);
      if (shortcut.length > 0) {
        const filtered = cannedResponses.filter(r =>
          r.shortcuts && r.shortcuts.toLowerCase().startsWith(shortcut.toLowerCase())
        );
        setFilteredCannedResponses(filtered);
        setShowCannedResponses(filtered.length > 0);
      } else {
        setShowCannedResponses(false);
      }
    } else {
      setShowCannedResponses(false);
    }
  }, [newMessage, cannedResponses]);

  // ─── WebSocket setup ─────────────────────────────────────────
  // Debounce helper to avoid flooding API with rapid socket events
  const refreshTimerRef = useRef(null);
  const refreshConversationsDebounced = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      fetchAllConversations(true);
    }, 300); // 300ms debounce — merges rapid events into one API call
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const socketUrl = window.location.hostname === 'localhost'
      ? 'http://localhost:3001'
      : 'https://agents.matix.one';

    socketRef.current = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    socketRef.current.on('connect', () => {
      console.log('✅ AllConversations: WebSocket connected');
      setSocketConnected(true);
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('❌ AllConversations: WebSocket disconnected:', reason);
      setSocketConnected(false);
    });

    socketRef.current.on('connect_error', (err) => {
      console.error('AllConversations: WebSocket error:', err.message);
      setSocketConnected(false);
    });

    // ── 1. New message in selected conversation (real-time chat) ──
    socketRef.current.on('new-message', (data) => {
      console.log('📩 New message event:', data.conversation_id);
      
      // 🔔 NOTIFICATION: New customer message (not in current chat)
      const isCurrentChat = selectedConversationRef.current?.id === data.conversation_id;
      const isCustomerMsg = data.message?.message_type === 'customer' || data.message?.message_type === 'user';
      if (isCustomerMsg && !isCurrentChat) {
        const customerName = data.message?.customer_name || data.customer_name || 'عميل';
        const msgPreview = (data.message?.message_text || data.message?.text || '').substring(0, 80);
        sendBrowserNotification(
          `💬 رسالة جديدة من ${customerName}`,
          msgPreview || 'رسالة جديدة',
        { tag: `msg-${data.conversation_id}`, conversationId: data.conversation_id, alwaysShow: true }
        );
      }

      // Update chat panel if this conversation is open - trigger fetch instead of direct insert
      if (selectedConversationRef.current && data.conversation_id === selectedConversationRef.current.id) {
        fetchMessages(data.conversation_id, true);
      }
      // Always refresh list to update message counts + unread badges
      refreshConversationsDebounced();
    });

    // ── 2. New assignment (sent to user-{agentId} room) ──
    // This fires when a conversation is assigned TO ME
    socketRef.current.on('new-assignment', (data) => {
      console.log('📋 New assignment for me:', data);
      sendBrowserNotification(
        '📋 محادثة جديدة مخصصة لك',
        `تم تخصيص محادثة ${data.conversationId || ''} لك`,
        { 
          tag: `assign-${data.conversationId}`, 
          conversationId: data.conversationId,
          urgent: true,
          alwaysShow: true
        }
      );
      refreshConversationsDebounced();
    });

    // ── 3. Conversation assigned (sent to role-admin/supervisor) ──
    // Admins/supervisors see all assignment changes
    socketRef.current.on('conversation-assigned', (data) => {
      console.log('📋 Conversation assigned:', data);
      refreshConversationsDebounced();
    });

    // ── 4. Conversation status changed (sent to room + role-admin) ──
    // Catches: assigned, pending, completed, normal status transitions
    socketRef.current.on('conversation-status-changed', (data) => {
      console.log('🔄 Status changed:', data.conversationId, '→', data.status);

      // If currently viewing this conversation and it was completed by another agent
      if (selectedConversation && data.conversationId === selectedConversation.id) {
        if (data.status === 'completed' || data.status === 'ended') {
          setSelectedConversation(null);
          setMessages([]);
          setIsMobileChat(false);
        } else {
          // Update status in the selected conversation
          setSelectedConversation(prev => prev ? { ...prev, status: data.status } : null);
        }
      }
      refreshConversationsDebounced();
    });

    // ── 5. Conversation completed event ──
    socketRef.current.on('conversation-completed', (data) => {
      console.log('✅ Conversation completed:', data);
      if (selectedConversation && data.conversation_id === selectedConversation.id) {
        setSelectedConversation(null);
        setMessages([]);
        setIsMobileChat(false);
      }
      refreshConversationsDebounced();
    });

    // ── 6. Queue update (broadcast to ALL agents) ──
    // This is the global "something changed" signal
    socketRef.current.on('queue-update', (data) => {
      console.log('🔔 Queue update received:', data);
      refreshConversationsDebounced();
    });

    // ── 7. Bot pause/resume events ──
    socketRef.current.on('bot-pause-warning', (data) => {
      console.log('⚠️ Bot pause warning:', data);
      refreshConversationsDebounced();
    });

    socketRef.current.on('bot-resumed', (data) => {
      console.log('🤖 Bot resumed:', data);
      if (selectedConversation && data.conversationId === selectedConversation.id) {
        setBotPaused(false);
      }
      refreshConversationsDebounced();
    });

    // ── 8. Server confirmation ──
    socketRef.current.on('connected', (data) => {
      console.log('🔌 Server confirmed:', data.userName, 'role:', data.userRole);
    });

// ── 9. 🔔 New customer message (global broadcast) ──
    socketRef.current.on('new-customer-message', (data) => {
      console.log('📩 New customer message (global):', data.conversation_id);
      const isCurrentChat = selectedConversationRef.current?.id === data.conversation_id;
      if (!isCurrentChat) {
        sendBrowserNotification(
          `💬 رسالة جديدة من ${data.customer_name || data.phone_number}`,
          data.message_text || 'رسالة جديدة',
          { tag: `msg-${data.conversation_id}`, conversationId: data.conversation_id }
        );
      }
      refreshConversationsDebounced();
    });


    // ── 9. 🔔 Customer requested agent transfer ──
    socketRef.current.on('customer-transfer-request', (data) => {
      console.log('🚨 Customer requested agent:', data);
      const customerName = data.customerName || data.phone_number || 'عميل';
      sendBrowserNotification(
        '🚨 عميل يطلب موظف',
        `${customerName} يحتاج مساعدة موظف`,
        { 
          tag: `transfer-${data.conversationId}`, 
          conversationId: data.conversationId,
          urgent: true,
          alwaysShow: true  // Show even if tab is focused
        }
      );
      refreshConversationsDebounced();
    });

    // ── 10. 🔔 New helpdesk ticket ──
    socketRef.current.on('new-helpdesk-ticket', (data) => {
      console.log('🎫 New helpdesk ticket:', data);
      sendBrowserNotification(
        '🎫 تذكرة جديدة',
        `${data.subject || 'تذكرة جديدة'} - ${data.clientName || 'عميل'}`,
        { 
          tag: `ticket-${data.ticketId}`,
          urgent: true,
          alwaysShow: true
        }
      );
    });

    // ── 11. 🔔 Agent status changed (online/offline) ──
    socketRef.current.on('agent-status-changed', (data) => {
      console.log('👤 Agent status changed:', data);
      const statusText = data.is_online ? 'أصبح متصل ✅' : 'أصبح غير متصل ⚫';
      sendBrowserNotification(
        `👤 ${data.agentName}`,
        statusText,
        { tag: `agent-status-${data.agentId}` }
      );
      // Refresh agents list
      fetchAgents();
    });

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  // Join conversation room when selected (for real-time messages)
  useEffect(() => {
    const convId = selectedConversationRef.current?.id;
    if (convId && socketRef.current && socketConnected) {
      console.log('Joining conversation room:', convId);
      socketRef.current.emit('join-conversation', convId, (response) => {
        console.log('Join response:', response);
      });
      return () => {
        console.log('Leaving conversation room:', convId);
        socketRef.current.emit('leave-conversation', convId);
      };
    }
  }, [selectedConversation?.id, socketConnected]); // ← ID only, not full object

  // ─── Initial load ────────────────────────────────────────────
  useEffect(() => {
    getUserInfo();
    fetchAllConversations();
    fetchAgents();
    fetchCannedResponses();
  }, []);

  // ─── Polling: conversations list (every 5s as fallback) ──────
  // WebSocket events trigger immediate refresh, but polling ensures
  // we catch changes even if socket events are missed (e.g. customer
  // messages to conversations we haven't joined)
  useEffect(() => {
    conversationPollingRef.current = setInterval(() => fetchAllConversations(true), 5000);
    return () => clearInterval(conversationPollingRef.current);
  }, []);

  // ─── Polling: messages in selected conversation (every 2s) ───
  const selectedConvId = selectedConversation?.id;
  useEffect(() => {
    if (messagePollingRef.current) clearInterval(messagePollingRef.current);
    if (selectedConvId) {
      // Don't poll immediately — initial load already fetched messages
      messagePollingRef.current = setInterval(pollForNewMessages, MESSAGES_POLL_INTERVAL);
    }
    return () => { if (messagePollingRef.current) clearInterval(messagePollingRef.current); };
  }, [selectedConvId]); // ← Only re-start when conversation ID changes, not on metadata updates

  // Scroll management: only scroll to bottom if user was already at bottom or it's initial load
  useLayoutEffect(() => {
    const prevLen = prevMessagesLenRef.current;
    const currLen = messages.length;
    prevMessagesLenRef.current = currLen;

    // Case 1: Initial load (went from 0 to N messages) — always scroll to bottom
    if (prevLen === 0 && currLen > 0) {
      scrollToBottom();
      return;
    }

    // Case 2: New messages arrived — scroll only if user was at bottom
    if (currLen > prevLen && isAtBottomRef.current) {
      scrollToBottom();
      return;
    }

    // Case 3: shouldScroll was explicitly set (e.g. after sending a message)
    if (shouldScrollRef.current) {
      scrollToBottom();
      shouldScrollRef.current = false;
      return;
    }

    // Case 4: No new messages or user scrolled up — do nothing, preserve position
  }, [messages]);

  // Safety net: restore scroll position on ANY re-render that wasn't caused by messages change
  // This handles re-renders from conversations list refresh, etc.
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (container && messages.length > 0 && savedScrollTopRef.current > 0) {
      // If scroll was at 0 but we had a saved position, something reset it — restore
      if (container.scrollTop === 0 && savedScrollTopRef.current > 100) {
        container.scrollTop = savedScrollTopRef.current;
      }
    }
  });

  // Handle navigation state (open specific conversation)
  useEffect(() => {
    const stateConvId = location.state?.openConversationId;
    if (stateConvId && conversations.length > 0 && !selectedConversation) {
      const conv = conversations.find(c => c.id === parseInt(stateConvId));
      if (conv) {
        handleConversationClick(conv);
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, conversations.length]);

  // Handle URL query param (from WhatsApp notification link)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const convId = params.get('conversation');
    if (convId && conversations.length > 0 && !selectedConversation) {
      const conv = conversations.find(c => c.id === parseInt(convId));
      if (conv) {
        handleConversationClick(conv);
        navigate(location.pathname, { replace: true });
      }
    }
  }, [location.search, conversations.length]);


  // ═══════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════

  // ─── Conversation List Item ──────────────────────────────────
  const ConversationItem = ({ conv }) => {
    const isSelected = selectedConversation?.id === conv.id;
    const isMyAssignment = conv.current_agent_name === currentAgentName;
    const sc = STATUS_CONFIG[conv.status] || STATUS_CONFIG.normal;
    const displayName = conv.user_name || 'Unknown Customer';
    const displayPhone = conv.phone_number || conv.session_id || '';
    const unread = conv.unread_count || 0;

    return (
      <div
        onClick={() => handleConversationClick(conv)}
        style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '13px 16px', cursor: 'pointer',
          background: isSelected ? t.selectedBg : 'transparent',
          borderLeft: isSelected ? '3px solid #6366f1'
            : conv.status === 'pending' ? '3px solid #f59e0b'
            : '3px solid transparent',
          borderBottom: `1px solid ${t.panelBorder}`,
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = t.cardHover; }}
        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Avatar */}
        <div style={{
          width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
          background: `linear-gradient(135deg, ${getAvatarColor(conv.id)}, ${getAvatarColor(conv.id + 3)})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '14px', fontWeight: 700, color: '#fff', letterSpacing: '0.5px',
        }}>
          {getInitials(displayName, displayPhone)}
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Row 1: Name + Time */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
            <span style={{
              color: unread > 0 ? t.textStrong : t.text,
              fontWeight: unread > 0 ? 600 : 400,
              fontSize: '14px',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: '170px',
            }}>
              {displayName !== 'Unknown Customer' ? displayName : displayPhone}
            </span>
            <span className={unread > 0 ? 'ct-green' : 'ct-faint'} style={{
              color: unread > 0 ? '#10b981' : t.textFaint,
              fontSize: '11px', flexShrink: 0, fontWeight: unread > 0 ? 600 : 400,
            }}>
              {getTimeSince(conv.last_activity || conv.started_at)}
            </span>
          </div>

          {/* Row 1.5: Phone if name shown */}
          {displayName !== 'Unknown Customer' && (
            <div className="ct-faint" style={{ color: t.textFaint, fontSize: '11px', marginBottom: '1px' }}>{displayPhone}</div>
          )}

          {/* Row 2: Language + Unread */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="ct-muted" style={{
              color: t.textMuted, fontSize: '12px',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: '180px',
            }}>
              {conv.language === 'ar' ? '🇸🇦 Arabic' : '🇬🇧 English'} • {conv.total_messages || 0} msgs
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              {conv.status === 'pending' && (
                <span className="pulse-dot" style={{
                  display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                  background: '#f59e0b',
                }}/>
              )}
              {unread > 0 && (
                <span style={{
                  background: '#10b981', color: '#fff',
                  fontSize: '11px', fontWeight: 700,
                  minWidth: 20, height: 20, borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 5px',
                }}>
                  {unread}
                </span>
              )}
            </div>
          </div>

          {/* Row 3: Status + Agent */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
            <span className={({'#22c55e':'ct-green','#10b981':'ct-green','#f59e0b':'ct-amber','#6b7280':'ct-muted','#818cf8':'ct-indigo'})[sc.color] || ''} style={{
              fontSize: '10px', fontWeight: 600,
              color: sc.color, background: sc.bg,
              padding: '1px 8px', borderRadius: 10,
              border: `1px solid ${sc.border}`,
              textTransform: 'uppercase', letterSpacing: '0.3px',
            }}>
              {sc.label}
            </span>
            {/* 🛒 Sales badge */}
            {conv.conversation_type === "sales" && (
              <span className="sales-badge-tag" style={{
                fontSize: "10px", fontWeight: 600,
                color: "#ffffff", background: "#dc2626",
                padding: "1px 8px", borderRadius: 10,
                border: "1px solid #b91c1c",
                textTransform: "uppercase", letterSpacing: "0.3px",
              }}>
                🛒 Sales
              </span>
            )}
            {/* Show agent for assigned conversations */}
            {conv.status === 'assigned' && conv.current_agent_name && (
              <span className="ct-green" style={{ fontSize: '10px', color: '#10b981', fontWeight: 500 }}>
                👤 {isMyAssignment ? 'You' : conv.current_agent_name}
              </span>
            )}
            {/* Show last agent for pending (escalated back) */}
            {conv.status === 'pending' && conv.last_agent_name && (
              <span className="ct-amber" style={{ fontSize: '10px', color: '#f59e0b' }}>
                ↩ {conv.last_agent_name}
              </span>
            )}
            {/* Show agent who handled completed/ended conversations */}
            {(conv.status === 'completed' || conv.status === 'ended') && (conv.current_agent_name || conv.previous_agent_name) && (
              <span className="ct-faint" style={{ fontSize: '10px', color: t.textFaint }}>
                👤 {(conv.current_agent_name || conv.previous_agent_name) === currentAgentName ? 'You' : (conv.current_agent_name || conv.previous_agent_name)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── Left Panel ──────────────────────────────────────────────
  const LeftPanel = () => (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column',
      background: t.panelBg,
      borderRight: `1px solid ${t.panelBorder}`,
    }}>
      {/* Header */}
      <div style={{ padding: '14px 14px 10px', borderBottom: `1px solid ${t.panelBorder}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div>
            <h2 style={{ color: t.textStrong, fontSize: '18px', fontWeight: 700, margin: 0 }}>
              Conversations
            </h2>
            <span className="ct-faint" style={{ color: t.textFaint, fontSize: '12px' }}>
              {filteredConversations.length} of {conversations.length}
              {totalUnread > 0 && <span className="ct-green" style={{ color: '#10b981', fontWeight: 600 }}> • {totalUnread} unread</span>}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {/* Socket status */}
            <div className={socketConnected ? 'ct-green' : 'ct-red'} style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '3px 8px', borderRadius: 12, fontSize: '10px', fontWeight: 600,
              background: socketConnected ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
              color: socketConnected ? '#10b981' : '#ef4444',
              border: `1px solid ${socketConnected ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            }}>
              <div style={{
                width: 6, height: 6, borderRadius: '50%',
                background: socketConnected ? '#10b981' : '#ef4444',
              }}/>
              {socketConnected ? 'Live' : 'Offline'}
            </div>
            <button
              onClick={() => fetchAllConversations()}
              style={{
                background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)',
                borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
                '--btn-c': '#818cf8', fontSize: '12px', fontWeight: 600,
              }}
            >
              ↻ Refresh
            </button>
          </div>
        </div>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: '10px' }}>
          <div className="ct-faint" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: t.textFaint, display: 'flex', alignItems: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          </div>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search by name or phone..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%', padding: '9px 12px 9px 38px',
              background: t.inputBg,
              border: `1px solid ${t.inputBorder}`,
              borderRadius: 10, color: t.textStrong, fontSize: '13px',
              outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Status Tabs */}
        <div style={{ display: 'flex', gap: '4px', overflowX: 'auto', paddingBottom: '4px', marginBottom: '6px' }}>
          {[
            { key: 'all', label: 'All' },
            { key: 'pending', label: 'Pending' },
            { key: 'assigned', label: 'Assigned' },
            { key: 'normal', label: 'AI Bot' },
            { key: 'completed', label: 'Closed' },
          ].map(tab => {
            const sc = STATUS_CONFIG[tab.key];
            const isActive = statusFilter === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                style={{
                  padding: '4px 10px', borderRadius: 8,
                  fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  background: isActive ? (sc?.bg || 'rgba(99,102,241,0.2)') : t.cardBg,
                  '--btn-c': isActive ? (sc?.color || '#818cf8') : t.textFaint,
                  border: isActive ? `1px solid ${sc?.border || 'rgba(99,102,241,0.3)'}` : '1px solid transparent',
                  transition: 'all 0.15s ease',
                }}
              >
                {tab.label}
                <span style={{ marginLeft: 3, fontSize: '10px', opacity: 0.8 }}>
                  {tab.key === 'completed' ? (statusCounts.completed || 0) + (statusCounts.ended || 0) : (statusCounts[tab.key] || 0)}
                </span>
              </button>
            );
          })}
        </div>

        {/* Assignment + Sort */}
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'Everyone' },
            { key: 'mine', label: 'My Chats' },
            { key: 'unassigned', label: 'Unassigned' },
            { key: 'unread', label: 'Unread' },
          ].map(af => (
            <button
              key={af.key}
              onClick={() => setAssignmentFilter(af.key)}
              style={{
                padding: '3px 8px', borderRadius: 6,
                fontSize: '11px', fontWeight: 500, cursor: 'pointer', border: 'none',
                background: assignmentFilter === af.key ? 'rgba(139,92,246,0.15)' : 'transparent',
                '--btn-c': assignmentFilter === af.key ? '#a78bfa' : t.textFaint,
              }}
            >
              {af.label}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <select
            className="ct-muted"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            style={{
              background: t.cardBg,
              border: `1px solid ${t.cardBorder}`,
              borderRadius: 6, padding: '2px 6px',
              color: t.textMuted, fontSize: '11px', cursor: 'pointer', outline: 'none',
            }}
          >
            <option value="latest">Latest</option>
            <option value="unread">Unread</option>
            <option value="messages">Messages</option>
          </select>
          <select
            className="ct-muted"
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            style={{
              background: t.cardBg,
              border: `1px solid ${t.cardBorder}`,
              borderRadius: 6, padding: '2px 6px',
              color: t.textMuted, fontSize: '11px', cursor: 'pointer', outline: 'none',
            }}
          >
            <option value="all">All Types</option>
            <option value="sales">🛒 Sales</option>
            <option value="technical">🔧 Technical</option>
          </select>
        </div>
      </div>

      {/* Conversation List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div className="ct-faint" style={{ padding: '40px', textAlign: 'center', color: t.textFaint }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }}></div>
            Loading conversations...
          </div>
        ) : error ? (
          <div className="ct-red" style={{ padding: '20px', textAlign: 'center', color: '#ef4444', fontSize: '13px' }}>
            Error: {error}
            <br/>
            <button onClick={() => fetchAllConversations()} className="ct-indigo" style={{ marginTop: 8, color: '#818cf8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
              Retry
            </button>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="ct-faint" style={{ padding: '40px 20px', textAlign: 'center', color: t.textFaint, fontSize: '13px' }}>
            No conversations match your filters
          </div>
        ) : (
          filteredConversations.map(conv => (
            <ConversationItem key={conv.id} conv={conv} />
          ))
        )}
      </div>
    </div>
  );

  // ─── Chat Header ─────────────────────────────────────────────
  const ChatHeader = () => {
    if (!selectedConversation) return null;
    const sc = STATUS_CONFIG[selectedConversation.status] || STATUS_CONFIG.normal;
    const displayName = selectedConversation.user_name || 'Unknown Customer';
    const displayPhone = selectedConversation.phone_number || selectedConversation.session_id || '';

    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '10px 16px',
        background: t.headerBg,
        borderBottom: `1px solid ${t.panelBorder}`,
        flexWrap: 'wrap',
      }}>
        {/* Mobile back */}
        <button
          onClick={() => { setIsMobileChat(false); setSelectedConversation(null); setMessages([]); }}
          className="mobile-back-btn"
          style={{ display: 'none', background: 'none', border: 'none', color: '#818cf8', cursor: 'pointer', padding: 4 }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>
        </button>

        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: `linear-gradient(135deg, ${getAvatarColor(selectedConversation.id)}, ${getAvatarColor(selectedConversation.id + 3)})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '13px', fontWeight: 700, color: '#fff',
        }}>
          {getInitials(displayName, displayPhone)}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: t.textStrong, fontWeight: 600, fontSize: '14px' }}>
            {displayName !== 'Unknown Customer' ? displayName : displayPhone}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '1px', flexWrap: 'wrap' }}>
            {displayName !== 'Unknown Customer' && (
              <span className="ct-faint" style={{ color: t.textFaint, fontSize: '11px' }}>{displayPhone}</span>
            )}
            <span className={({'#22c55e':'ct-green','#10b981':'ct-green','#f59e0b':'ct-amber','#6b7280':'ct-muted','#818cf8':'ct-indigo'})[sc.color] || ''} style={{
              fontSize: '10px', fontWeight: 600,
              color: sc.color, background: sc.bg,
              padding: '1px 7px', borderRadius: 10,
              border: `1px solid ${sc.border}`,
            }}>
              {sc.label}
            </span>
            {/* 🛒 Sales badge */}
            {selectedConversation.conversation_type === "sales" && (
              <span className="sales-badge-tag" style={{
                fontSize: "10px", fontWeight: 600,
                color: "#ffffff", background: "#dc2626",
                padding: "1px 8px", borderRadius: 10,
                border: "1px solid #b91c1c",
                textTransform: "uppercase", letterSpacing: "0.3px",
              }}>
                🛒 Sales
              </span>
            )}
            <span className="ct-faint" style={{ color: t.textFaint, fontSize: '11px' }}>
              {selectedConversation.language === 'ar' ? 'Arabic' : 'English'} • {selectedConversation.total_messages || 0} msgs
            </span>
            {selectedConversation.current_agent_name && (
              <span className="ct-faint" style={{ color: t.textFaint, fontSize: '11px' }}>
                👤 {selectedConversation.current_agent_name === currentAgentName ? 'You' : selectedConversation.current_agent_name}
              </span>
            )}
            {!selectedConversation.current_agent_name && selectedConversation.previous_agent_name && (
              <span className="ct-faint" style={{ color: t.textFaint, fontSize: '11px' }}>
                👤 {selectedConversation.previous_agent_name === currentAgentName ? 'You' : selectedConversation.previous_agent_name} (prev)
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Bot toggle */}
          {(selectedConversation.status !== 'completed' && selectedConversation.status !== 'ended') && (
            <button onClick={toggleBot} style={{
              padding: '6px 12px', borderRadius: 8, fontSize: '11px', fontWeight: 600, cursor: 'pointer',
              background: botPaused ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
              '--btn-c': botPaused ? '#f87171' : '#10b981',
              border: `1px solid ${botPaused ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`,
            }}>
              {botPaused ? '🤖 Resume Bot' : '⏸ Pause Bot'}
            </button>
          )}

          {/* Toggle Type */}
          <button onClick={toggleConversationType} style={{
            padding: '6px 12px', borderRadius: 8, fontSize: '11px', fontWeight: 600, cursor: 'pointer',
            background: selectedConversation.conversation_type === 'sales' ? 'rgba(220,38,38,0.15)' : 'rgba(59,130,246,0.15)',
            '--btn-c': selectedConversation.conversation_type === 'sales' ? '#f87171' : '#60a5fa',
            border: `1px solid ${selectedConversation.conversation_type === 'sales' ? 'rgba(220,38,38,0.3)' : 'rgba(59,130,246,0.3)'}`,
          }}>
            {selectedConversation.conversation_type === 'sales' ? '🔧 To Technical' : '🛒 To Sales'}
          </button>

          {/* Take */}
          {selectedConversation.status === 'pending' && (
            <button
              onClick={(e) => handleTakeConversation(selectedConversation.id, e)}
              disabled={takingConversation === selectedConversation.id}
              style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                border: 'none', borderRadius: 8, padding: '6px 14px',
                color: '#fff', '--btn-c': '#fff', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
                opacity: takingConversation === selectedConversation.id ? 0.6 : 1,
              }}
            >
              {takingConversation === selectedConversation.id ? 'Taking...' : '✓ Take'}
            </button>
          )}

          {/* Assign */}
          {(userRole === 'supervisor' || userRole === 'admin') && (
            <button onClick={() => { setShowAssignModal(true); setSelectedAgent(''); }} style={{
              background: 'rgba(99,102,241,0.15)',
              border: '1px solid rgba(99,102,241,0.3)',
              borderRadius: 8, padding: '6px 12px',
              color: '#818cf8', '--btn-c': '#818cf8', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
            }}>
              👤 Assign
            </button>
          )}

          {/* Complete */}
          {selectedConversation.status !== 'completed' && selectedConversation.status !== 'ended' && (
            <button onClick={completeConversation} style={{
              background: 'rgba(107,114,128,0.15)',
              border: '1px solid rgba(107,114,128,0.3)',
              borderRadius: 8, padding: '6px 12px',
              color: '#9ca3af', '--btn-c': '#9ca3af', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
            }}>
              ✓ Close
            </button>
          )}
        </div>
      </div>
    );
  };

  // ─── Messages Area ───────────────────────────────────────────
  const MessagesArea = () => (
    <div ref={messagesContainerRef} onScroll={handleMessagesScroll} style={{
      flex: 1, overflowY: 'auto',
      padding: '16px',
      background: t.msgBg,
    }}>
      {loadingMessages ? (
        <div className="ct-faint" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', color: t.textFaint }}>
          <div className="spinner"></div>
          <p style={{ marginTop: '12px', fontSize: '13px' }}>Loading messages...</p>
        </div>
      ) : messages.length === 0 ? (
        <div className="ct-faint" style={{ textAlign: 'center', padding: '60px 20px', color: t.textFaint }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>💬</div>
          <p className="ct-muted" style={{ fontSize: '13px', color: t.textMuted }}>No messages yet</p>
        </div>
      ) : (
        <>
          {messages.map((message, index) => {
            const isAgent = message.message_type === 'agent';
            const isAI = message.message_type === 'ai';
            const isCustomer = !isAgent && !isAI;

            // Date separator
            const currentDateKey = getDateKey(message.timestamp);
            const prevDateKey = index > 0 ? getDateKey(messages[index - 1].timestamp) : null;
            const showDateSeparator = index === 0 || currentDateKey !== prevDateKey;

            return (
              <React.Fragment key={message.id || index}>
                {showDateSeparator && (
                  <div style={{
                    display: 'flex', justifyContent: 'center', margin: '16px 0 12px',
                  }}>
                    <span className="date-separator" style={{
                      background: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(0,0,0,0.06)',
                      color: isDark ? '#a5b4fc' : '#54656f',
                      padding: '4px 14px', borderRadius: 8,
                      fontSize: '11px', fontWeight: 600,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                      letterSpacing: '0.2px',
                    }}>
                      {getDateLabel(message.timestamp)}
                    </span>
                  </div>
                )}
              <div
                className={`message-item ${isAgent ? 'agent' : isAI ? 'ai' : 'customer'}`}
                style={{
                  display: 'flex',
                  justifyContent: isCustomer ? 'flex-start' : 'flex-end',
                  marginBottom: '8px',
                }}
              >
                <div
                  className="message-bubble"
                  style={{
                    maxWidth: '75%', padding: '8px 12px',
                    borderRadius: isCustomer ? '4px 12px 12px 12px' : '12px 4px 12px 12px',
                    backgroundColor: isAgent ? '#25d366' : isAI ? '#dcf8c6' : '#ffffff',
                    color: isAgent ? '#ffffff' : '#000000',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    wordBreak: 'break-word',
                  }}
                >
                  {/* Sender tag */}
                  {isAI && (
                    <div style={{ fontSize: '10px', fontWeight: 700, color: '#6366f1', marginBottom: '3px' }}>
                      🤖 AI Bot
                    </div>
                  )}

                  {/* Media attachments */}
                  {message.media_url && message.media_type === 'image' && (
                    <div style={{ marginBottom: '6px' }}>
                      <img 
                        src={message.media_url} 
                        alt="Customer attachment"
                        style={{ maxWidth: '100%', maxHeight: 250, borderRadius: 8, cursor: 'pointer', display: 'block' }}
                        onClick={() => window.open(message.media_url, '_blank')}
                      />
                    </div>
                  )}
                  {message.media_url && message.media_type === 'document' && (
                    <div style={{ marginBottom: '6px', padding: '6px 10px', backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>📄</span>
                      <a href={message.media_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: '#1976d2' }}>
                        View Document
                      </a>
                    </div>
                  )}
                  {message.media_url && message.media_type === 'voice' && (
                    <div style={{ marginBottom: '6px' }}>
                      <audio controls style={{ maxWidth: '100%', height: 36 }}>
                        <source src={message.media_url} type="audio/ogg" />
                      </audio>
                    </div>
                  )}

                  <div style={{ fontSize: '14px', lineHeight: '1.45', whiteSpace: 'pre-wrap' }}>
                    {message.message_text || message.message}
                  </div>

                  {/* Feedback badge */}
                  {isAI && message.feedback_type && (
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: '3px',
                      marginTop: '4px', padding: '2px 6px', borderRadius: '6px',
                      fontSize: '10px', fontWeight: 600,
                      backgroundColor: message.feedback_type === 'helpful' ? 'rgba(34,197,94,0.15)'
                        : message.feedback_type === 'not_helpful' ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)',
                      color: message.feedback_type === 'helpful' ? '#22c55e'
                        : message.feedback_type === 'not_helpful' ? '#f59e0b' : '#ef4444',
                    }}>
                      {message.feedback_type === 'helpful' ? '👍' : message.feedback_type === 'not_helpful' ? '👎' : '❌'}
                      {message.feedback_type === 'helpful' ? 'مفيد' : message.feedback_type === 'not_helpful' ? 'غير مفيد' : 'خطأ'}
                    </div>
                  )}

                  <div style={{
                    fontSize: '10px', color: isAgent ? 'rgba(255,255,255,0.7)' : '#667781',
                    textAlign: 'right', marginTop: '2px',
                  }}>
                    {formatMessageTime(message.timestamp)}
                    {isAgent && <span style={{ marginLeft: '4px' }}>✔✔</span>}
                  </div>
                </div>
              </div>
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} />
        </>
      )}
    </div>
  );

  // ─── Input Area ──────────────────────────────────────────────
  const InputArea = () => (
    <div style={{
      padding: '10px 14px',
      background: t.headerBg,
      borderTop: `1px solid ${t.panelBorder}`,
      position: 'relative',
    }}>
      {/* Canned Responses Dropdown */}
      {showCannedResponses && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 14, right: 14,
          background: t.modalPanel,
          border: `1px solid ${t.panelBorder}`,
          borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          maxHeight: 200, overflowY: 'auto', zIndex: 100, marginBottom: 4,
        }}>
          {filteredCannedResponses.map(response => (
            <div
              key={response.id}
              onClick={() => insertCannedResponse(response)}
              style={{
                padding: '10px 14px',
                borderBottom: `1px solid ${t.panelBorder}`,
                cursor: 'pointer',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(99,102,241,0.15)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div style={{ fontWeight: 600, fontSize: '12px', color: t.textStrong, marginBottom: 2 }}>
                {response.title}
                <span className="ct-muted" style={{ marginLeft: 8, fontSize: '10px', color: t.textMuted }}>/{response.shortcuts}</span>
              </div>
              <div style={{ fontSize: '11px', color: t.text }}>
                {response.content_en || response.content_ar}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
        style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0]) handleMediaUpload(e.target.files[0]); }}
      />

      {/* Media preview */}
      {mediaPreview && (
        <div style={{
          padding: '8px 12px', marginBottom: '4px',
          background: t.inputBg, borderRadius: 12,
          border: `1px solid ${t.inputBorder}`,
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          {mediaPreview.type === 'image' ? (
            <img src={mediaPreview.url} alt="Preview" style={{ height: 60, borderRadius: 8, objectFit: 'cover' }} />
          ) : mediaPreview.type === 'voice' ? (
            <audio controls src={mediaPreview.url} style={{ height: 36 }} />
          ) : (
            <div style={{ fontSize: '13px', color: t.textStrong }}>📄 {mediaPreview.name}</div>
          )}
          <div style={{ flex: 1, fontSize: '12px', color: t.textMuted }}>
            {mediaPreview.type === 'image' ? 'Image ready to send' : 'Document ready to send'}
          </div>
          <button onClick={cancelMediaPreview} style={{
            background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px', padding: '4px',
          }}>✕</button>
          <button onClick={sendMedia} disabled={uploadingMedia} style={{
            background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
            border: 'none', color: '#fff', borderRadius: 20, padding: '6px 16px',
            fontSize: '13px', fontWeight: 600, cursor: uploadingMedia ? 'not-allowed' : 'pointer',
          }}>
            {uploadingMedia ? '⏳ Sending...' : '➤ Send'}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {/* Attachment button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingMedia || isRecording}
          title="Attach image or document"
          style={{
            width: 40, height: 40, borderRadius: '50%',
            background: t.inputBg, border: `1px solid ${t.inputBorder}`,
            color: t.textMuted, fontSize: '18px',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'all 0.2s ease',
          }}
        >
          📎
        </button>

        {/* Recording indicator OR normal input */}
        {isRecording ? (
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: '10px',
            padding: '8px 16px', background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.3)', borderRadius: 24,
          }}>
            <div style={{
              width: 10, height: 10, borderRadius: '50%', background: '#ef4444',
              animation: 'pulse 1s infinite',
            }} />
            <span style={{ color: '#ef4444', fontSize: '14px', fontWeight: 600 }}>
              {formatRecordingTime(recordingTime)}
            </span>
            <span style={{ color: t.textMuted, fontSize: '12px', flex: 1 }}>Recording...</span>
            <button onClick={cancelRecording} style={{
              background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px',
            }}>✕</button>
            <button onClick={stopRecording} style={{
              background: '#ef4444', border: 'none', color: '#fff', borderRadius: '50%',
              width: 32, height: 32, cursor: 'pointer', fontSize: '14px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>⏹</button>
          </div>
        ) : (
        <>
        <input
          ref={messageInputRef}
          type="text"
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          onKeyPress={e => {
            if (e.key === 'Enter' && !sendingMessage && newMessage.trim()) {
              sendMessage();
            }
          }}
          placeholder='Type a message... (/ for canned responses)'
          disabled={sendingMessage}
          style={{
            flex: 1, padding: '11px 16px',
            background: t.inputBg,
            border: `1px solid ${t.inputBorder}`,
            borderRadius: 24, color: t.textStrong, fontSize: '13px',
            outline: 'none',
          }}
        />
        <button
          onClick={sendMessage}
          disabled={sendingMessage || !newMessage.trim()}
          style={{
            width: 44, height: 44, borderRadius: '50%',
            background: sendingMessage || !newMessage.trim()
              ? t.cardBg
              : 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
            border: 'none',
            color: sendingMessage || !newMessage.trim() ? '#4b5563' : '#fff',
            '--btn-c': sendingMessage || !newMessage.trim() ? '#4b5563' : '#fff',
            fontSize: '18px',
            cursor: sendingMessage || !newMessage.trim() ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'all 0.2s ease',
          }}
        >
          {sendingMessage ? '⏳' : '➤'}
        </button>
        </>
        )}

        {/* Mic button */}
        {!isRecording && (
        <button
          onClick={startRecording}
          disabled={uploadingMedia || sendingMessage || mediaPreview}
          title="Record voice message"
          style={{
            width: 40, height: 40, borderRadius: '50%',
            background: t.inputBg, border: `1px solid ${t.inputBorder}`,
            color: t.textMuted, fontSize: '18px',
            cursor: (uploadingMedia || sendingMessage || mediaPreview) ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'all 0.2s ease',
            opacity: (uploadingMedia || sendingMessage || mediaPreview) ? 0.4 : 1,
          }}
        >
          🎤
        </button>
        )}
      </div>
    </div>
  );

  // ─── Right Panel (Empty state or Chat) ───────────────────────
  const RightPanel = () => {
    if (!selectedConversation) {
      return (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          background: t.emptyBg, gap: '14px',
        }}>
          <div style={{
            width: 100, height: 100, borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.1))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(99,102,241,0.15)',
          }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(129,140,248,0.4)" strokeWidth="1.2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ color: t.text, fontWeight: 600, fontSize: '17px', margin: '0 0 6px' }}>
              MatixERP Agent
            </h3>
            <p className="ct-faint" style={{ color: t.textFaint, fontSize: '13px', margin: 0, maxWidth: 280, lineHeight: 1.5 }}>
              Select a conversation from the left to start chatting.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <span className="ct-amber" style={{
              background: 'rgba(245,158,11,0.1)', color: '#f59e0b',
              padding: '5px 12px', borderRadius: 8, fontSize: '12px', fontWeight: 600,
              border: '1px solid rgba(245,158,11,0.2)',
            }}>
              {statusCounts.pending || 0} Pending
            </span>
            <span className="ct-green" style={{
              background: 'rgba(16,185,129,0.1)', color: '#10b981',
              padding: '5px 12px', borderRadius: 8, fontSize: '12px', fontWeight: 600,
              border: '1px solid rgba(16,185,129,0.2)',
            }}>
              {statusCounts.assigned || 0} Assigned
            </span>
            <span className="ct-indigo" style={{
              background: 'rgba(99,102,241,0.1)', color: '#818cf8',
              padding: '5px 12px', borderRadius: 8, fontSize: '12px', fontWeight: 600,
              border: '1px solid rgba(99,102,241,0.2)',
            }}>
              {conversations.length} Total
            </span>
          </div>
        </div>
      );
    }

    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {ChatHeader()}
        {MessagesArea()}
        {InputArea()}
      </div>
    );
  };

  // ─── Assign Modal ────────────────────────────────────────────
  const AssignModal = () => {
    if (!showAssignModal || !selectedConversation) return null;

    return (
      <div
        onClick={() => setShowAssignModal(false)}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: t.modalBg, backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: t.modalPanel, borderRadius: 16,
            border: `1px solid ${t.cardBorder}`,
            padding: '24px', width: 380, maxWidth: '90vw',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          }}
        >
          <h3 style={{ color: t.textStrong, margin: '0 0 4px', fontSize: '16px', fontWeight: 600 }}>
            Assign Conversation
          </h3>
          <p className="ct-faint" style={{ color: t.textFaint, fontSize: '12px', margin: '0 0 16px' }}>
            {selectedConversation.phone_number || selectedConversation.session_id}
          </p>

          <select
            value={selectedAgent}
            onChange={e => setSelectedAgent(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px', fontSize: '13px',
              background: t.inputBg,
              border: `1px solid ${t.inputBorder}`,
              borderRadius: 10, color: t.textStrong, marginBottom: '16px',
              outline: 'none',
            }}
          >
            <option value="">Select an agent...</option>
            {agents.map(agent => (
              <option key={agent.id} value={agent.id}>
                {agent.full_name} ({agent.email})
              </option>
            ))}
          </select>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              className="ct-muted"
              onClick={() => { setShowAssignModal(false); setSelectedAgent(''); }}
              style={{
                flex: 1, padding: '10px',
                background: t.cardBg,
                border: `1px solid ${t.cardBorder}`,
                borderRadius: 10, '--btn-c': t.textMuted, color: t.textMuted, fontSize: '13px',
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleAssignToAgent}
              disabled={!selectedAgent}
              style={{
                flex: 1, padding: '10px',
                background: !selectedAgent ? t.cardBg : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                border: 'none', borderRadius: 10,
                color: !selectedAgent ? t.textFaint : '#fff',
                '--btn-c': !selectedAgent ? t.textFaint : '#fff',
                fontSize: '13px', fontWeight: 600,
                cursor: !selectedAgent ? 'not-allowed' : 'pointer',
              }}
            >
              Assign
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Main Return ─────────────────────────────────────────────
  return (
    <Layout>
      {/* 🔔 Notification Permission Banner */}
      {notifPermission === 'default' && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
          padding: '8px 16px',
          background: 'linear-gradient(135deg, #4f46e5, #6366f1)',
          color: '#fff', fontSize: '13px', fontWeight: 500,
        }}>
          <span>🔔 فعّل التنبيهات لتصلك إشعارات الرسائل والتذاكر الجديدة</span>
          <button
            onClick={() => {
              if (typeof Notification !== 'undefined') {
                Notification.requestPermission().then(perm => setNotifPermission(perm));
              }
            }}
            style={{
              padding: '4px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.3)',
              background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
            }}
          >
            تفعيل
          </button>
          <button
            onClick={() => setNotifPermission('dismissed')}
            style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '16px', padding: '0 4px',
            }}
          >
            ✕
          </button>
        </div>
      )}
      <div className={`all-conv-container ${isDark ? 'conv-dark' : 'conv-light'}`} style={{
        height: 'calc(100vh - 60px)',
        display: 'flex',
        background: t.bg,
        overflow: 'hidden',
        color: t.text,
      }}>
        {/* Styles */}
        <style>{`
          .all-conv-container ::placeholder { color: ${t.textFaint} !important; }
          .all-conv-container ::-webkit-scrollbar { width: 5px; }
          .all-conv-container ::-webkit-scrollbar-track { background: transparent; }
          .all-conv-container ::-webkit-scrollbar-thumb { background: ${t.scrollThumb}; border-radius: 10px; }
          .all-conv-container select option { background: ${t.selectBg}; color: ${t.text}; }

          .pulse-dot {
            animation: pulse-dot 2s ease-in-out infinite;
          }
          @keyframes pulse-dot {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(0.8); }
          }

          .spinner {
            width: 36px; height: 36px;
            border: 3px solid rgba(99,102,241,0.1);
            border-top-color: #6366f1;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }
          @keyframes spin { to { transform: rotate(360deg); } }

          @keyframes slideInRight {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }

          /* Split panel layout */
          .conv-left-panel {
            width: 380px;
            min-width: 310px;
            flex-shrink: 0;
            height: 100%;
          }
          .conv-right-panel {
            flex: 1;
            display: flex;
            flex-direction: column;
            height: 100%;
            overflow: hidden;
          }

          /* Message bubbles - let inline styles take precedence */
          .all-conv-container .message-item .message-bubble,
          .all-conv-container .message-item .message-bubble * {
            /* Let inline styles take precedence */
          }

          /* ═══════════════════════════════════════════════════════════════
             THEME OVERRIDES
             dark-theme-override.css uses class-based selectors (0,3,1)
             We use #root ID selector for guaranteed win (1,x,y always > 0,x,y)
             ═══════════════════════════════════════════════════════════════ */

          /* ─── LIGHT THEME ─── */
          #root .all-conv-container.conv-light { color: #1f2937 !important; }
          #root .all-conv-container.conv-light div { color: #1f2937 !important; }
          #root .all-conv-container.conv-light span { color: #1f2937 !important; }
          #root .all-conv-container.conv-light p { color: #1f2937 !important; }
          #root .all-conv-container.conv-light label { color: #1f2937 !important; }
          #root .all-conv-container.conv-light h2 { color: #111827 !important; }
          #root .all-conv-container.conv-light h3 { color: #111827 !important; }
          /* Button colors via CSS custom properties (only way to beat !important) */
          #root .all-conv-container.conv-light button { color: var(--btn-c, #1f2937) !important; }
          #root .all-conv-container.conv-dark button { color: var(--btn-c, #d1d5db) !important; }
          /* Muted/faint buttons */
          #root .conv-light button.ct-faint { color: #9ca3af !important; }
          #root .conv-light button.ct-muted { color: #6b7280 !important; }
          #root .conv-light button.ct-indigo { color: #818cf8 !important; }
          #root .conv-light button.ct-red { color: #ef4444 !important; }
          #root .conv-dark button.ct-faint { color: #6b7280 !important; }
          #root .conv-dark button.ct-muted { color: #9ca3af !important; }
          #root .conv-dark button.ct-indigo { color: #818cf8 !important; }
          #root .conv-dark button.ct-red { color: #ef4444 !important; }
          #root .all-conv-container.conv-light input { color: #1f2937 !important; background-color: #f3f4f6 !important; }
          #root .all-conv-container.conv-light select { color: #1f2937 !important; }
          #root .all-conv-container.conv-light select option { background: #ffffff !important; color: #1f2937 !important; }

          /* Muted/faint text via utility classes */
          #root .conv-light .ct-faint { color: #9ca3af !important; }
          #root .conv-light .ct-muted { color: #6b7280 !important; }
          #root .conv-light .ct-green { color: #10b981 !important; }
          #root .conv-light .ct-amber { color: #f59e0b !important; }
          #root .conv-light .ct-red { color: #ef4444 !important; }
          #root .conv-light .ct-indigo { color: #818cf8 !important; }
          #root .conv-light .ct-purple { color: #a78bfa !important; }

          /* ─── DARK THEME ─── */
          #root .all-conv-container.conv-dark { color: #d1d5db !important; }
          #root .all-conv-container.conv-dark div { color: #d1d5db !important; }
          #root .all-conv-container.conv-dark span:not(.sales-badge-tag) { color: #d1d5db !important; }
          #root .all-conv-container.conv-dark p { color: #d1d5db !important; }
          #root .all-conv-container.conv-dark label { color: #d1d5db !important; }
          #root .all-conv-container.conv-dark h2 { color: #e5e7eb !important; }
          #root .all-conv-container.conv-dark h3 { color: #e5e7eb !important; }

          #root .conv-dark .ct-faint { color: #6b7280 !important; }
          #root .conv-dark .ct-muted { color: #9ca3af !important; }
          #root .conv-dark .ct-green { color: #10b981 !important; }
          #root .conv-dark .ct-amber { color: #f59e0b !important; }
          #root .conv-dark .ct-red { color: #ef4444 !important; }
          #root .conv-dark .ct-indigo { color: #818cf8 !important; }
          #root .conv-dark .ct-purple { color: #a78bfa !important; }

          /* ─── MESSAGE BUBBLE PROTECTION (HIGHEST specificity) ─── */
          /* Customer bubble = white bg, black text */
          #root .all-conv-container.conv-dark .message-item.customer .message-bubble,
          #root .all-conv-container.conv-light .message-item.customer .message-bubble {
            background: #ffffff !important;
            background-color: #ffffff !important;
            color: #000000 !important;
          }
          /* AI Bot bubble = light green bg, black text */
          #root .all-conv-container.conv-dark .message-item.ai .message-bubble,
          #root .all-conv-container.conv-light .message-item.ai .message-bubble {
            background: #dcf8c6 !important;
            background-color: #dcf8c6 !important;
            color: #000000 !important;
          }
          /* Agent bubble = green bg, white text */
          #root .all-conv-container.conv-dark .message-item.agent .message-bubble,
          #root .all-conv-container.conv-light .message-item.agent .message-bubble {
            background: #25d366 !important;
            background-color: #25d366 !important;
            color: #ffffff !important;
          }
          /* All text inside bubbles inherits from parent */
          #root .all-conv-container.conv-dark .message-item.customer .message-bubble div,
          #root .all-conv-container.conv-dark .message-item.customer .message-bubble span,
          #root .all-conv-container.conv-dark .message-item.customer .message-bubble p,
          #root .all-conv-container.conv-light .message-item.customer .message-bubble div,
          #root .all-conv-container.conv-light .message-item.customer .message-bubble span,
          #root .all-conv-container.conv-light .message-item.customer .message-bubble p,
          #root .all-conv-container.conv-dark .message-item.ai .message-bubble div,
          #root .all-conv-container.conv-dark .message-item.ai .message-bubble span,
          #root .all-conv-container.conv-dark .message-item.ai .message-bubble p,
          #root .all-conv-container.conv-light .message-item.ai .message-bubble div,
          #root .all-conv-container.conv-light .message-item.ai .message-bubble span,
          #root .all-conv-container.conv-light .message-item.ai .message-bubble p {
            color: #000000 !important;
            background: transparent !important;
            background-color: transparent !important;
          }
          #root .all-conv-container.conv-dark .message-item.agent .message-bubble div,
          #root .all-conv-container.conv-dark .message-item.agent .message-bubble span,
          #root .all-conv-container.conv-dark .message-item.agent .message-bubble p,
          #root .all-conv-container.conv-light .message-item.agent .message-bubble div,
          #root .all-conv-container.conv-light .message-item.agent .message-bubble span,
          #root .all-conv-container.conv-light .message-item.agent .message-bubble p {
            color: #ffffff !important;
            background: transparent !important;
            background-color: transparent !important;
          }
          /* Alignment: customer = LEFT, agent+AI = RIGHT */
          #root .all-conv-container .message-item.customer {
            justify-content: flex-start !important;
          }
          #root .all-conv-container .message-item.agent,
          #root .all-conv-container .message-item.ai {
            justify-content: flex-end !important;
          }
          /* AI Bot label */
          #root .all-conv-container.conv-dark .message-item.ai .message-bubble div[style*="6366f1"],
          #root .all-conv-container.conv-light .message-item.ai .message-bubble div[style*="6366f1"] {
            color: #6366f1 !important;
          }
          /* Timestamps - agent */
          #root .all-conv-container.conv-dark .message-item.agent .message-bubble div[style*="textAlign"],
          #root .all-conv-container.conv-light .message-item.agent .message-bubble div[style*="textAlign"] {
            color: rgba(255,255,255,0.7) !important;
          }
          /* Timestamps - customer/AI */
          #root .all-conv-container.conv-dark .message-item:not(.agent) .message-bubble div[style*="textAlign"],
          #root .all-conv-container.conv-light .message-item:not(.agent) .message-bubble div[style*="textAlign"] {
            color: #667781 !important;
          }
          /* Feedback badges - color AND background */
          #root .all-conv-container .message-item .message-bubble div[style*="22c55e"] { color: #22c55e !important; background-color: rgba(34,197,94,0.15) !important; }
          #root .all-conv-container .message-item .message-bubble div[style*="f59e0b"] { color: #f59e0b !important; background-color: rgba(245,158,11,0.15) !important; }
          #root .all-conv-container .message-item .message-bubble div[style*="ef4444"] { color: #ef4444 !important; background-color: rgba(239,68,68,0.15) !important; }

          /* Date separator protection */
          #root .all-conv-container.conv-dark .date-separator {
            color: #a5b4fc !important;
            background: rgba(99,102,241,0.15) !important;
            background-color: rgba(99,102,241,0.15) !important;
          }
          #root .all-conv-container.conv-light .date-separator {
            color: #54656f !important;
            background: rgba(0,0,0,0.06) !important;
            background-color: rgba(0,0,0,0.06) !important;
          }

          /* Mobile */
          @media (max-width: 768px) {
            .conv-left-panel {
              width: 100% !important;
              display: ${isMobileChat ? 'none' : 'block'} !important;
            }
            .conv-right-panel {
              display: ${isMobileChat ? 'flex' : 'none'} !important;
            }
            .mobile-back-btn {
              display: flex !important;
            }
          }

          @media (min-width: 769px) {
            .mobile-back-btn {
              display: none !important;
            }
          }
        `}</style>

        {/* Left Panel */}
        <div className="conv-left-panel">
          {LeftPanel()}
        </div>

        {/* Right Panel */}
        <div className="conv-right-panel">
          {RightPanel()}
        </div>

        {/* Assign Modal */}
        <AssignModal />
      </div>
    </Layout>
  );
};

export default AllConversations;